import React from 'react';

import { jwtDecode } from 'jwt-decode';

import { acError } from '@looko-corp/acloset-react-kit/src/sentry';

import { useSetUserAuth } from '@/state/cookieContext';
import type { DecodeToken } from '@/state/userAuthContext';

import { WEBVIEW_EVENT_TYPE, webViewEventSchema } from './config/web-view-event-schema';

/**
 * 웹뷰 auth provider
 **/
export function WebViewAuthProvider({ children }: { children: React.ReactNode }) {
  const [done, setDone] = React.useState(false);

  const setUserAuth = useSetUserAuth();

  const handler = React.useCallback(
    (e: Event) => {
      if (e instanceof MessageEvent) {
        try {
          if (typeof e.data !== 'string') {
            throw new Error('MessageEvent data가 string이 아닙니다.');
          }

          const event = webViewEventSchema.safeParse(JSON.parse(e.data));

          if (!event.success) {
            throw new Error('safeParse 실패');
          }

          const safeParseData = event.data;

          if (safeParseData.type !== WEBVIEW_EVENT_TYPE.AUTH) {
            return;
          }

          setUserAuth({
            userID: safeParseData.data.userID,
            accessToken: safeParseData.data.accessToken,
            refreshToken: safeParseData.data.refreshToken,
            decodeToken: jwtDecode<DecodeToken>(safeParseData.data.accessToken),
            roles: [safeParseData.data.userRole],
          });

          setDone(true);
        } catch (err) {
          acError(`WebView Auth login 실패 ${err}`, err);
        }
      }
    },
    [setUserAuth],
  );

  React.useEffect(() => {
    if (window.ReactNativeWebView == null) {
      return;
    }

    document.addEventListener('message', handler);
    window.addEventListener('message', handler);

    return () => {
      document.removeEventListener('message', handler);
      window.removeEventListener('message', handler);
    };
  }, [handler]);

  if (window.ReactNativeWebView == null) {
    return children;
  }

  return done ? children : null;
}
